import type React from "react"
import { useCallback, useEffect, useRef, useState } from "react"
import { useRouter } from "next/router"

// Long enough to carry the pointer across a gap between the toggle, the peek,
// and a layer the peek opened, short enough that leaving reads as dismissing.
const PEEK_CLOSE_DELAY_MS = 200

/**
 * Layers a panel can open outside its own DOM — Radix popovers and menus, and
 * Clerk's organization switcher.
 */
const PORTAL_LAYER_SELECTOR = [
  "[data-radix-popper-content-wrapper]",
  "[data-floating-ui-portal]",
  ".cl-organizationSwitcherPopoverCard",
].join(", ")

const findPortalLayer = (target: EventTarget | null) =>
  target instanceof Element ? target.closest(PORTAL_LAYER_SELECTOR) : null

interface PeekTriggerProps {
  onPointerEnter: (event: React.PointerEvent) => void
  onPointerLeave: (event: React.PointerEvent) => void
}

/**
 * The hidden sidebar shown on hover: hovering the toggle opens it, and it stays
 * while the pointer is on the toggle, the peek, or a layer the peek opened.
 * Leaving all of them, pressing outside, Escape, a navigation, or the peek
 * becoming unavailable closes it.
 *
 * Only a mouse peeks. A touch has no hover, so it goes straight to the
 * toggle's click.
 */
export function useSidebarPeek(enabled: boolean): {
  isPeeking: boolean
  closePeek: () => void
  /** Keeps the toggle from peeking until the pointer has left it once. */
  suppressPeekUntilPointerLeaves: () => void
  triggerProps: PeekTriggerProps | undefined
  surfaceProps: { ref: React.RefObject<HTMLDivElement | null> }
} {
  const router = useRouter()
  const [isOpen, setIsOpen] = useState(false)
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const surfaceRef = useRef<HTMLDivElement | null>(null)
  const isPointerOnTriggerRef = useRef(false)
  const isPeeking = enabled && isOpen

  const cancelClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }, [])

  // An already pending close is left to run rather than restarted, so a
  // pointer moving across the page keeps no peek alive.
  const scheduleClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      return
    }
    closeTimeoutRef.current = setTimeout(() => {
      closeTimeoutRef.current = null
      setIsOpen(false)
    }, PEEK_CLOSE_DELAY_MS)
  }, [])

  const closePeek = useCallback(() => {
    cancelClose()
    setIsOpen(false)
  }, [cancelClose])

  // Set when the pointer hides the sidebar: the show toggle then appears right
  // under it, and a peek there would undo the hide the user just asked for.
  const isSuppressedRef = useRef(false)

  const suppressPeekUntilPointerLeaves = useCallback(() => {
    isSuppressedRef.current = true
  }, [])

  const onTriggerPointerEnter = useCallback(
    (event: React.PointerEvent) => {
      isPointerOnTriggerRef.current = true
      if (event.pointerType !== "mouse" || isSuppressedRef.current) {
        return
      }
      cancelClose()
      setIsOpen(true)
    },
    [cancelClose],
  )

  const onTriggerPointerLeave = useCallback(() => {
    isPointerOnTriggerRef.current = false
    isSuppressedRef.current = false
    scheduleClose()
  }, [scheduleClose])

  useEffect(() => cancelClose, [cancelClose])

  // A peek that became unavailable — the viewport narrowed past the breakpoint,
  // the sidebar was pinned — is closed rather than held, so it cannot reappear
  // later without a fresh hover.
  useEffect(() => {
    if (!enabled) {
      closePeek()
    }
  }, [enabled, closePeek])

  // A row the peek does not own — a module's contributed row, a Sofia thread —
  // navigates without telling it, so the route itself closes the peek.
  useEffect(() => {
    closePeek()
  }, [router.asPath, closePeek])

  useEffect(() => {
    if (!isPeeking) {
      return
    }

    // Layers already open when the peek opened belong to the page, not to it;
    // anything that appears since was opened from the peek.
    const layersBeforePeek = new Set(
      document.querySelectorAll(PORTAL_LAYER_SELECTOR),
    )
    const isPeekOwned = (target: EventTarget | null) => {
      if (target instanceof Node && surfaceRef.current?.contains(target)) {
        return true
      }
      const layer = findPortalLayer(target)
      return layer !== null && !layersBeforePeek.has(layer)
    }

    // Tracked on the document rather than on the peek, because the layers it
    // opens are portalled outside it and a pointer can reach one across a gap.
    const onPointerOver = (event: PointerEvent) => {
      // A modal layer opening from the peek turns off hit-testing on the body,
      // so the pointer reads as over the root until the layer mounts; that is
      // a layer in transit, not the page.
      if (
        event.target === document.documentElement ||
        event.target === document.body
      ) {
        return
      }
      if (isPointerOnTriggerRef.current || isPeekOwned(event.target)) {
        cancelClose()
      } else {
        scheduleClose()
      }
    }
    // Leaving the window fires no `pointerover` anywhere.
    const onPointerOut = (event: PointerEvent) => {
      if (event.relatedTarget === null) {
        scheduleClose()
      }
    }
    const onPointerDown = (event: PointerEvent) => {
      if (!isPointerOnTriggerRef.current && !isPeekOwned(event.target)) {
        closePeek()
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closePeek()
      }
    }
    document.addEventListener("pointerover", onPointerOver)
    document.addEventListener("pointerout", onPointerOut)
    document.addEventListener("pointerdown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("pointerover", onPointerOver)
      document.removeEventListener("pointerout", onPointerOut)
      document.removeEventListener("pointerdown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [isPeeking, cancelClose, scheduleClose, closePeek])

  return {
    isPeeking,
    closePeek,
    suppressPeekUntilPointerLeaves,
    triggerProps: enabled
      ? {
          onPointerEnter: onTriggerPointerEnter,
          onPointerLeave: onTriggerPointerLeave,
        }
      : undefined,
    surfaceProps: { ref: surfaceRef },
  }
}
